import { useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { selectSelectedItem } from '@reducers/machines';
import { buildVersionLabel } from '@components/ncFileVersions/helpers/buildVersionLabel';
import { buildFavoriteCaption } from '@components/ncFileVersions/helpers/buildFavoriteCaption';

import { runLinkNcFileToJob } from '@components/search/operations/components/StartJobPopup/components/LinkCurrentNcFile/helpers/runLinkNcFileToJob';
import { LINK_STATUS } from '@helpers';
import type { LinkStatusValue } from '@components/search/operations/components/StartJobPopup/types';
import type { LinkCurrentNcFileProps } from '@components/search/operations/components/StartJobPopup/components/LinkCurrentNcFile';

export type UseManageLinkCurrentNcFileParams = Pick<LinkCurrentNcFileProps, 'productionOperationId' | 'onLinked'>;

export const useManageLinkCurrentNcFile = ({ productionOperationId, onLinked }: UseManageLinkCurrentNcFileParams) => {
  const dispatch = useAppDispatch();
  const selectedItem = useSelector(selectSelectedItem);
  const machineStatus = selectedItem?.machineStatus;

  const ncFileId = machineStatus?.ncFileId;
  const ncFileName = machineStatus?.ncFile;
  const ncFileNumber = machineStatus?.ncFileNumber;
  const ncFileIsFavorite = machineStatus?.ncFileIsFavorite;
  const productionBatchId = machineStatus?.productionBatchId;
  const machineName = selectedItem?.shortName;

  // All from machineStatus so the caption stays live with OnUpdatedMachineStatus (same source as the live screen).
  const versionLabel = ncFileNumber ? buildVersionLabel({ sequenceNumber: ncFileNumber, isFavorite: !!ncFileIsFavorite }) : '';
  const favoriteCaption = buildFavoriteCaption({ machine: machineName ? { shortName: machineName } : null, isFavorite: !!ncFileIsFavorite });

  const [linkStatus, setLinkStatus] = useState<LinkStatusValue>(LINK_STATUS.Idle);
  const [linkError, setLinkError] = useState<string | undefined>();

  const handleLink = () => {
    if (!ncFileId) return;
    runLinkNcFileToJob({ dispatch, ncFileId, productionOperationId, productionBatchId, setLinkStatus, setLinkError, onLinked });
  };

  return {
    ncFileId,
    ncFileName,
    versionLabel,
    favoriteCaption,
    linkStatus,
    linkError,
    handleLink,
  };
};
